/**
 * Display-only FX via Shopify App Proxy → GET /v1/rates.
 * The app proxy injects Admin Markets currencies. Browser cache follows API `expiresAt`.
 *
 * Markup: `[data-money]` on price nodes; value is amount in shop billing currency cents
 * (not the formatted text). Liquid/JS still render shop-currency text for first paint.
 *
 * Display currency: `localStorage` preference, else shop billing currency (no browser locale guess).
 */

import { localStorageKey } from '@theme/utilities';

const PREFERENCE_KEY = localStorageKey('display_currency');
const CURRENCY_CHANGE_EVENT = 'mf-currency:change';
const RATES_READY_EVENT = 'mf-currency:rates-ready';
const CURRENCY_CODE_REGEX = /^[A-Z]{3}$/;

/** @type {Record<string, string>} */
const REGION_CURRENCY = {
  AE: 'AED',
  AR: 'ARS',
  AT: 'EUR',
  AU: 'AUD',
  BE: 'EUR',
  BD: 'BDT',
  BG: 'BGN',
  BH: 'BHD',
  BR: 'BRL',
  CA: 'CAD',
  CH: 'CHF',
  CL: 'CLP',
  CN: 'CNY',
  CO: 'COP',
  CZ: 'CZK',
  DE: 'EUR',
  DK: 'DKK',
  EG: 'EGP',
  ES: 'EUR',
  FI: 'EUR',
  FR: 'EUR',
  GB: 'GBP',
  GR: 'EUR',
  HK: 'HKD',
  HU: 'HUF',
  ID: 'IDR',
  IE: 'EUR',
  IL: 'ILS',
  IN: 'INR',
  IT: 'EUR',
  JP: 'JPY',
  KE: 'KES',
  KR: 'KRW',
  KW: 'KWD',
  LK: 'LKR',
  MX: 'MXN',
  MY: 'MYR',
  NG: 'NGN',
  NL: 'EUR',
  NO: 'NOK',
  NP: 'NPR',
  NZ: 'NZD',
  PH: 'PHP',
  PK: 'PKR',
  PL: 'PLN',
  PT: 'EUR',
  QA: 'QAR',
  RO: 'RON',
  RU: 'RUB',
  SA: 'SAR',
  SE: 'SEK',
  SG: 'SGD',
  TH: 'THB',
  TR: 'TRY',
  TW: 'TWD',
  UA: 'UAH',
  US: 'USD',
  VN: 'VND',
  ZA: 'ZAR',
};

let loggedFetchError = false;
let applyingPrices = false;
let observerStarted = false;

/** @type {FxRatesPayload | null} */
let ratesCache = null;

/** @type {string | null} */
let sessionCurrency = null;

/**
 * @typedef {Object} FxRateRow
 * @property {string} quote
 * @property {number} rate
 * @property {string} [date]
 */

/**
 * @typedef {Object} FxRatesPayload
 * @property {string} base
 * @property {FxRateRow[]} rates
 * @property {string} [expiresAt]
 * @property {string[]} [currencies]
 * @property {string} [purpose]
 * @property {string} [detectedCurrency]
 */

/**
 * @returns {boolean}
 */
function isDesignMode() {
  return Boolean(window.Shopify?.designMode);
}

/**
 * @returns {Record<string, unknown>}
 */
function getThemeSettings() {
  const theme = /** @type {{ settings?: Record<string, unknown> }} */ (
    window.Theme
  );
  return theme?.settings && typeof theme.settings === 'object'
    ? theme.settings
    : {};
}

/**
 * @returns {string}
 */
function getShopCurrency() {
  const settings = getThemeSettings();
  const fromSettings = String(settings.shopCurrency || '').toUpperCase();
  if (CURRENCY_CODE_REGEX.test(fromSettings)) return fromSettings;

  const fromShopify = String(
    window.Shopify?.currency?.active || ''
  ).toUpperCase();
  if (CURRENCY_CODE_REGEX.test(fromShopify)) return fromShopify;

  return 'USD';
}

/**
 * @returns {string}
 */
function getRatesPath() {
  const path = String(getThemeSettings().currencyRatesPath || '').trim();
  return path || '/apps/currency/rates';
}

/**
 * @param {string} base
 * @returns {string}
 */
function ratesStorageKey(base) {
  return localStorageKey(`fx_rates_${base.toUpperCase()}`);
}

/**
 * @param {string} key
 * @returns {string | null}
 */
function readLocalStorageItem(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {string} key
 * @param {string} value
 */
function writeLocalStorageItem(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore quota / privacy mode
  }
}

/**
 * @param {string} key
 */
function removeLocalStorageItem(key) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/**
 * @returns {string[]}
 */
function getMarketCurrencies() {
  const raw = getThemeSettings().mfMarketCurrencies;
  const list = Array.isArray(raw) ? raw : [];
  /** @type {string[]} */
  const codes = [];

  list.forEach((value) => {
    const code = normalizeCurrencyCode(String(value || ''));
    if (code && !codes.includes(code)) codes.push(code);
  });

  const countries = getThemeSettings().mfMarketCountries;
  if (Array.isArray(countries)) {
    countries.forEach((value) => {
      const region = String(value || '')
        .trim()
        .toUpperCase();
      const mapped = REGION_CURRENCY[region];
      if (mapped && !codes.includes(mapped)) codes.push(mapped);
    });
  }

  const shopCurrency = getShopCurrency();
  if (!codes.includes(shopCurrency)) codes.unshift(shopCurrency);
  return codes;
}

/**
 * @param {string} value
 * @returns {string | null}
 */
function normalizeCurrencyCode(value) {
  const code = String(value || '')
    .trim()
    .toUpperCase();
  return CURRENCY_CODE_REGEX.test(code) ? code : null;
}

/**
 * @param {unknown} raw
 * @returns {FxRatesPayload | null}
 */
export function normalizeRatesPayload(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const record = /** @type {Record<string, unknown>} */ (raw);
  const nested =
    record.data && typeof record.data === 'object'
      ? /** @type {Record<string, unknown>} */ (record.data)
      : record;

  const base = normalizeCurrencyCode(String(nested.base || nested.from || ''));
  if (!base) return null;

  /** @type {FxRateRow[]} */
  const rates = [];
  const source = nested.rates ?? nested.quotes;

  if (Array.isArray(source)) {
    source.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      const row = /** @type {Record<string, unknown>} */ (entry);
      const quote = normalizeCurrencyCode(
        String(row.quote || row.currency || row.code || '')
      );
      const rate = Number(row.rate ?? row.value);
      if (!quote || !Number.isFinite(rate)) return;
      rates.push({
        quote,
        rate,
        date: typeof row.date === 'string' ? row.date : undefined,
      });
    });
  } else if (source && typeof source === 'object') {
    Object.entries(/** @type {Record<string, unknown>} */ (source)).forEach(
      ([code, value]) => {
        const quote = normalizeCurrencyCode(code);
        const rate = Number(value);
        if (!quote || !Number.isFinite(rate)) return;
        rates.push({ quote, rate });
      }
    );
  }

  const detectedCurrency = normalizeCurrencyCode(
    String(
      nested.detectedCurrency ||
        nested.detected_currency ||
        nested.visitorCurrency ||
        nested.currency ||
        ''
    )
  );

  const expiresAt = String(nested.expiresAt || nested.expires_at || '');
  /** @type {string[]} */
  const currencies = [];
  const listed = nested.currencies;
  if (Array.isArray(listed)) {
    listed.forEach((value) => {
      const code = normalizeCurrencyCode(String(value || ''));
      if (code && !currencies.includes(code)) currencies.push(code);
    });
  }

  return {
    base,
    rates,
    expiresAt,
    currencies: currencies.length ? currencies : undefined,
    detectedCurrency: detectedCurrency || undefined,
  };
}

/**
 * @param {FxRatesPayload} ratesData
 * @returns {Set<string>}
 */
function availableCurrencySet(ratesData) {
  const codes = new Set([String(ratesData.base).toUpperCase()]);
  ratesData.rates.forEach((row) => {
    const quote = normalizeCurrencyCode(row.quote);
    if (quote) codes.add(quote);
  });
  return codes;
}

/**
 * @returns {string[]}
 */
export function getAvailableCurrencies() {
  const codes = new Set(getMarketCurrencies());
  (ratesCache?.currencies || []).forEach((code) => {
    const normalized = normalizeCurrencyCode(code);
    if (normalized) codes.add(normalized);
  });
  return [...codes].sort();
}

/**
 * @param {FxRatesPayload} ratesData
 * @returns {string}
 */
function resolveDisplayCurrency(ratesData) {
  const available = availableCurrencySet(ratesData);
  const shopCurrency = getShopCurrency();

  if (sessionCurrency && available.has(sessionCurrency)) {
    return sessionCurrency;
  }

  if (!isDesignMode()) {
    const stored = normalizeCurrencyCode(
      readLocalStorageItem(PREFERENCE_KEY) || ''
    );
    if (stored && available.has(stored)) return stored;
  }

  return shopCurrency;
}

/**
 * @returns {string}
 */
export function getDisplayCurrency() {
  if (ratesCache) return resolveDisplayCurrency(ratesCache);

  if (!isDesignMode()) {
    const stored = normalizeCurrencyCode(
      readLocalStorageItem(PREFERENCE_KEY) || ''
    );
    if (stored) return stored;
  }

  return getShopCurrency();
}

/**
 * @param {string} code
 */
export function setDisplayCurrency(code) {
  const normalized = normalizeCurrencyCode(code);
  if (!normalized) return;

  sessionCurrency = normalized;

  if (!isDesignMode()) {
    writeLocalStorageItem(PREFERENCE_KEY, normalized);
  }

  void applyDisplayPrices();
  window.dispatchEvent(
    new CustomEvent(CURRENCY_CHANGE_EVENT, { detail: { currency: normalized } })
  );
}

/**
 * @param {string} raw
 * @param {boolean} allowStale
 * @returns {FxRatesPayload | null}
 */
function parseCachedRates(raw, allowStale) {
  try {
    const parsed = normalizeRatesPayload(JSON.parse(raw));
    if (!parsed) return null;

    if (!allowStale && parsed.expiresAt) {
      const expiresAt = new Date(parsed.expiresAt).getTime();
      if (Number.isFinite(expiresAt) && Date.now() >= expiresAt) {
        return null;
      }
    }

    return parsed;
  } catch {
    return null;
  }
}

/**
 * @param {string} base
 * @param {{ allowStale?: boolean }} [options]
 * @returns {FxRatesPayload | null}
 */
function readCachedRates(base, options = {}) {
  const key = ratesStorageKey(base);
  const raw = readLocalStorageItem(key);
  if (!raw) return null;

  const parsed = parseCachedRates(raw, Boolean(options.allowStale));
  if (!parsed && !options.allowStale) {
    removeLocalStorageItem(key);
  }
  return parsed;
}

/**
 * @param {string} base
 * @param {FxRatesPayload} data
 */
function writeCachedRates(base, data) {
  writeLocalStorageItem(ratesStorageKey(base), JSON.stringify(data));
}

/**
 * @param {FxRatesPayload} ratesData
 * @param {string} quote
 * @returns {number | null}
 */
function findRate(ratesData, quote) {
  const code = normalizeCurrencyCode(quote);
  if (!code) return null;

  const base = String(ratesData.base || '').toUpperCase();
  if (code === base) return 1;

  const row = ratesData.rates.find(
    (entry) => String(entry.quote || '').toUpperCase() === code
  );
  const rate = Number(row?.rate);
  return Number.isFinite(rate) ? rate : null;
}

/**
 * @param {number | string | undefined} cents
 * @param {string} quote
 * @param {FxRatesPayload} ratesData
 * @returns {number | null}
 */
export function convertCents(cents, quote, ratesData) {
  const amount = Number(cents);
  if (Number.isNaN(amount)) return null;

  const rate = findRate(ratesData, quote);
  if (rate == null) return null;

  return (amount / 100) * rate;
}

/**
 * @param {number} majorUnits
 * @param {string} currency
 * @returns {string}
 */
function formatMoney(majorUnits, currency) {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency,
    }).format(majorUnits);
  } catch {
    return majorUnits.toFixed(2);
  }
}

/**
 * @param {HTMLElement} node
 * @param {string} text
 */
function setNodeMoneyText(node, text) {
  const prefix = node.querySelector('.product-price__prefix');
  if (prefix) {
    Array.from(node.childNodes).forEach((child) => {
      if (child !== prefix) child.remove();
    });
    node.append(document.createTextNode(text));
    return;
  }

  node.textContent = text;
}

/**
 * Rewrites `[data-money]` text using display currency; reads cents from `dataset.money`.
 *
 * @param {ParentNode | undefined} root
 * @param {FxRatesPayload} ratesData
 * @param {string} displayCurrency
 */
function applyToRoot(root, ratesData, displayCurrency) {
  const scope = root || document;
  const nodes = scope.querySelectorAll('[data-money]');

  nodes.forEach((node) => {
    if (!(node instanceof HTMLElement)) return;

    const converted = convertCents(
      node.dataset.money,
      displayCurrency,
      ratesData
    );
    if (converted == null) return;

    setNodeMoneyText(node, formatMoney(converted, displayCurrency));
  });

  scope.querySelectorAll('[data-mf-disclaimer-currency]').forEach((node) => {
    if (node instanceof HTMLElement) {
      node.textContent = displayCurrency;
    }
  });
}

function dispatchRatesReady() {
  window.dispatchEvent(
    new CustomEvent(RATES_READY_EVENT, {
      detail: {
        currencies: getAvailableCurrencies(),
        displayCurrency: getDisplayCurrency(),
      },
    })
  );
}

/**
 * @param {ParentNode} [root]
 * @returns {Promise<void>}
 */
export async function applyDisplayPrices(root) {
  if (applyingPrices) return;

  const shopCurrency = getShopCurrency();
  const ratesData =
    ratesCache ||
    readCachedRates(shopCurrency) ||
    readCachedRates(shopCurrency, { allowStale: true }) ||
    (await fetchRates(shopCurrency));

  if (!ratesData) return;

  ratesCache = ratesData;
  const displayCurrency = resolveDisplayCurrency(ratesData);
  sessionCurrency = displayCurrency;

  applyingPrices = true;
  try {
    applyToRoot(root || document, ratesData, displayCurrency);
  } finally {
    applyingPrices = false;
  }
}

/**
 * @param {string} base
 * @returns {Promise<FxRatesPayload | null>}
 */
async function fetchRates(base) {
  const fresh = readCachedRates(base);
  if (fresh) {
    ratesCache = fresh;
    return fresh;
  }

  try {
    const params = new URLSearchParams({ base });
    const quotes = getMarketCurrencies().filter((code) => code !== base);
    if (quotes.length) params.set('quotes', quotes.join(','));

    const response = await fetch(`${getRatesPath()}?${params.toString()}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`Rates unavailable (${response.status})`);
    }

    const parsed = normalizeRatesPayload(await response.json());
    if (!parsed) {
      throw new Error('Invalid rates payload');
    }

    ratesCache = parsed;
    writeCachedRates(base, parsed);
    return parsed;
  } catch (error) {
    if (!loggedFetchError) {
      loggedFetchError = true;
      console.warn('[mf-currency] Failed to load exchange rates', error);
    }

    const stale = readCachedRates(base, { allowStale: true });
    if (stale) ratesCache = stale;
    return stale;
  }
}

/**
 * @param {ParentNode} [root]
 */
function scheduleApply(root) {
  window.requestAnimationFrame(() => {
    void applyDisplayPrices(root);
  });
}

function startReapplyListeners() {
  if (observerStarted) return;
  observerStarted = true;

  let timer = 0;
  const observer = new MutationObserver(() => {
    if (applyingPrices) return;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      void applyDisplayPrices();
    }, 50);
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  document.addEventListener('cart-drawer:rendered', (event) => {
    const target = event instanceof CustomEvent ? event.detail?.target : null;
    scheduleApply(target instanceof Element ? target : undefined);
  });
  document.addEventListener('cart-page:rendered', (event) => {
    const target = event instanceof CustomEvent ? event.detail?.target : null;
    scheduleApply(target instanceof Element ? target : undefined);
  });
  document.addEventListener('product:variant-change', () => scheduleApply());
}

/**
 * @returns {Promise<void>}
 */
export async function initCurrencyConverter() {
  const settings = getThemeSettings();
  if (settings.currencyConversionService !== 'mf_app') return;

  const shopCurrency = getShopCurrency();
  const ratesData = await fetchRates(shopCurrency);
  if (!ratesData) return;

  await applyDisplayPrices();
  startReapplyListeners();
  dispatchRatesReady();
}

void initCurrencyConverter();
